// Display helpers for the Vehicle Changelog section of the user detail page.
//
// A change log entry is one item of the vehicle change logs table (partition
// key vehicle_id, sort key modified_on in epoch milliseconds; verified live
// 2026-09-10) plus the vehicle's plate. Its `changes` attribute is a nested
// map of field name to {old, new}, written by the vehicle table's change
// feed. That shape renders as one line per field; anything else falls back
// to one line per leaf under a dotted path, so an unexpected shape still
// shows rather than disappearing.

import { VehicleChangeLogEntry } from '../types/vehicle'
import { convertDateFormat } from './formatDate'

export interface ChangeLine {
    path: string
    value: string
}

// Rendered for a missing or blank leaf so a cleared field still reads as a change.
export const EMPTY_VALUE_LABEL = '(empty)'

// Between the old and the new value of one field.
export const CHANGE_ARROW = '→'

// What an entry's event_type says happened to its vehicle. The change feed
// writes INSERT or ADD for a vehicle added (every field, old side empty),
// MODIFY for an edit (only the fields that changed) and REMOVE for a vehicle
// deleted (every field, new side empty); DELETE reads as a removal too. Any
// other event type is still shown as written, as 'other'.
export type ChangeLogEventKind = 'added' | 'modified' | 'removed' | 'other'

const EVENT_KINDS: Record<string, ChangeLogEventKind> = {
    INSERT: 'added',
    ADD: 'added',
    MODIFY: 'modified',
    REMOVE: 'removed',
    DELETE: 'removed',
}

export const changeLogEventKind = (eventType: unknown): ChangeLogEventKind => {
    if (typeof eventType !== 'string') return 'other'
    const key = eventType.trim().toUpperCase()
    return Object.prototype.hasOwnProperty.call(EVENT_KINDS, key) ? EVENT_KINDS[key] : 'other'
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

const isLeaf = (value: unknown): boolean => !isPlainObject(value) && !Array.isArray(value)

const isEmptyLeaf = (value: unknown): boolean => value === null || value === undefined || value === ''

const formatLeaf = (value: unknown): string => (isEmptyLeaf(value) ? EMPTY_VALUE_LABEL : String(value))

// A {old, new} pair of leaves -- either side may be missing or null.
const isOldNewPair = (value: Record<string, unknown>): boolean => {
    const keys = Object.keys(value)
    return keys.length > 0 && keys.every((key) => (key === 'old' || key === 'new') && isLeaf(value[key]))
}

// A field of a vehicle added has nothing on its old side, so it reads as the
// value it was added with. Every other pair keeps its arrow, like an edit: a
// field of a vehicle removed shows the value it had before the removal and the
// empty one after, and a pair with both sides set keeps it whatever the event,
// so no value is ever hidden.
const formatPair = (pair: Record<string, unknown>, kind: ChangeLogEventKind): string => {
    if (kind === 'added' && isEmptyLeaf(pair.old)) return formatLeaf(pair.new)
    return `${formatLeaf(pair.old)} ${CHANGE_ARROW} ${formatLeaf(pair.new)}`
}

const flatten = (changes: unknown, prefix: string, kind: ChangeLogEventKind): ChangeLine[] => {
    if (changes === undefined || (changes === null && prefix === '')) return []
    if (Array.isArray(changes)) {
        if (changes.length === 0) return prefix ? [{ path: prefix, value: EMPTY_VALUE_LABEL }] : []
        return changes.flatMap((item, index) => flatten(item, `${prefix}[${index}]`, kind))
    }
    if (isPlainObject(changes)) {
        if (isOldNewPair(changes)) {
            return [{ path: prefix, value: formatPair(changes, kind) }]
        }
        const keys = Object.keys(changes)
        if (keys.length === 0) return prefix ? [{ path: prefix, value: EMPTY_VALUE_LABEL }] : []
        return keys.flatMap((key) => flatten(changes[key], prefix ? `${prefix}.${key}` : key, kind))
    }
    return [{ path: prefix, value: formatLeaf(changes) }]
}

// One line per changed field ("old → new"; for a vehicle added, the value it
// was added with -- see formatPair), or per leaf of any other nested value;
// maps nest with dots, lists with [index]. A leaf at the root has an empty
// path. An absent root value yields no lines, and an empty map or list below
// the root is one line saying so.
export const flattenChanges = (changes: unknown, eventType?: unknown): ChangeLine[] =>
    flatten(changes, '', changeLogEventKind(eventType))

// modified_on is the table's sort key, epoch milliseconds. Epoch seconds and
// date strings are accepted too, in case a writer ever stores them that way;
// null when the value is none of those.
export const formatChangeLogTime = (value: unknown): string | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        // 1e11 milliseconds is 1973, 1e11 seconds is 5138.
        const ms = value < 1e11 ? value * 1000 : value
        return convertDateFormat(new Date(ms).toISOString())
    }
    if (typeof value === 'string') {
        if (/^\d{9,13}$/.test(value)) return formatChangeLogTime(Number(value))
        if (value.trim() !== '' && !Number.isNaN(Date.parse(value))) return convertDateFormat(value)
    }
    return null
}

export const changeLogTimestamp = (entry: VehicleChangeLogEntry): string | null =>
    formatChangeLogTime(entry.modified_on)
